import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import { SimpleTag } from '@atlaskit/tag';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { toLabels } from '../../../../../../model/card/utils';
import type { ExtraFieldText } from '../../../../../../model/extra-fields/types';
import { FIELD_DEFAULT_VALUE } from '../constants';
import { isDefault, parseContent } from '../utils';

type Props = { field: ExtraFieldText; isLite?: boolean };

const parseLabels = (text: string | undefined): string[] => {
	const content = parseContent(text);
	return isDefault(content) ? [] : toLabels(content);
};

const ItemRenderer = ({ item, label }: { item: string; label: string }) => (
	<SimpleTag aria-label={label} key={item} text={item} />
);

export function LabelsField({ field, isLite }: Props) {
	const content = parseLabels(field.text);
	const wrap = (children: ReactNode) =>
		isLite ? <>{children}</> : <ToolTip content={field.label}>{children}</ToolTip>;

	if (isEmpty(content)) {
		return wrap(
			<Container aria-hidden="true" isDefault>
				{FIELD_DEFAULT_VALUE}
			</Container>,
		);
	}

	return wrap(
		<Container aria-hidden="true" isDefault={false}>
			{content.map((item, i) => (
				<ItemRenderer key={i} item={item} label={item} />
			))}
		</Container>,
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ isDefault: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N100),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontStyle: (props) => (props.isDefault ? 'italic' : 'inherit'),
	minWidth: 0,
	maxWidth: '100%',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	position: 'relative',
	'&::after': {
		content: '',
		left: 0,
		top: 0,
		display: 'block',
		height: '100%',
		width: '100%',
		position: 'absolute',
		background:
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			'linear-gradient(to right, transparent 90%, var(--jsw-card-background-color, white) 100%)',
		backgroundAttachment: 'local',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		marginTop: 0,
	},
});
